import { ExternalLinkIcon, FileTextIcon, SearchIcon } from 'lucide-react'
import type { ReactNode } from 'react'
import {
  type LakeChunkHit,
  type LakeDocumentHit,
  type LakeHybridSearchArgs,
  type LakeHybridSearchResult,
  normalizeDisplayFilename,
  scoreColor,
} from '@/features/agent/tools/lake-hybrid-search'
import { useToolCardOpen } from '@/features/agent/tools/useToolCardOpen'
import { cn } from '@/utils/cn'
import { HIT_META_GROUP_CLASS, HIT_NAME_ROOM } from './_shared/hit-row'
import { ToolCallCard, ToolCallHeader, type ToolCallStatus } from './_shared/ToolCallHeader'

export interface LakeHybridSearchViewProps {
  args: LakeHybridSearchArgs
  parsed: LakeHybridSearchResult | null
  running: boolean
  errored: boolean
  /** Open a lake document detail sheet. Supplied by the connected tool. */
  onOpenDocument: (documentId: string) => void
  /** Force the card expanded even with no body. Defaults to false; the card
   *  auto-expands when it has results and stays collapsed for empty or failed
   *  calls. Stories pass true to render the expanded body. */
  defaultOpen?: boolean
}

/** Pure presentation for a `lake_hybrid_search` tool-result card. */
export function LakeHybridSearchView({
  args,
  parsed,
  running,
  errored,
  onOpenDocument,
  defaultOpen = false,
}: LakeHybridSearchViewProps) {
  const documentHits = parsed?.document_hits ?? []
  const chunkHits = parsed?.chunk_hits ?? []
  const totalDocuments = parsed?.total_documents ?? documentHits.length
  const totalChunks = parsed?.total_chunks ?? chunkHits.length
  const total = totalDocuments + totalChunks
  const hasBody = !running && (errored || documentHits.length > 0 || chunkHits.length > 0)
  const { open, onToggle } = useToolCardOpen(hasBody, defaultOpen, errored)

  const headerStatus: ToolCallStatus = running ? 'running' : errored ? 'error' : 'complete'

  const label = running
    ? 'Searching the lake for'
    : errored
      ? 'Failed —'
      : total === 0
        ? 'No lake matches for'
        : `${total} lake match${total === 1 ? '' : 'es'} for`

  const description = (
    <>
      {label}{' '}
      <code className="ml-0.5 rounded bg-muted px-1.5 py-0.5 font-mono text-[11px] text-foreground">
        "{args.query}"
      </code>
    </>
  )

  return (
    <ToolCallCard>
      <ToolCallHeader
        status={headerStatus}
        icon={SearchIcon}
        toolName="lake_hybrid_search"
        description={description}
        expandable={hasBody}
        open={open}
        onToggle={onToggle}
      />
      {open && (
        <div className="flex flex-col gap-3 border-t border-border px-3 py-2">
          {parsed?.error && (
            <p className="whitespace-pre-wrap text-xs text-muted-foreground">{parsed.error}</p>
          )}

          {documentHits.length > 0 && (
            <Section title="Documents" count={documentHits.length}>
              {documentHits.map((hit, i) => (
                <DocumentHitRow
                  key={hit.document_id || i}
                  hit={hit}
                  onOpenDocument={onOpenDocument}
                />
              ))}
            </Section>
          )}

          {chunkHits.length > 0 && (
            <Section title="Document content" count={chunkHits.length}>
              {chunkHits.map((hit, i) => (
                <ChunkHitRow key={hit.chunk_id || i} hit={hit} onOpenDocument={onOpenDocument} />
              ))}
            </Section>
          )}
        </div>
      )}
    </ToolCallCard>
  )
}

function DocumentHitRow({
  hit,
  onOpenDocument,
}: {
  hit: LakeDocumentHit
  onOpenDocument: (documentId: string) => void
}) {
  return (
    <HitButton
      onClick={() => hit.document_id && onOpenDocument(hit.document_id)}
      disabled={!hit.document_id}
    >
      <FileTextIcon className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" />
      <div className="flex min-w-0 flex-col gap-1 max-md:flex-1">
        <div className="flex flex-wrap items-center gap-1.5 text-xs">
          <span
            className={cn(
              'truncate font-medium',
              hit.label ? HIT_NAME_ROOM.icon : HIT_NAME_ROOM.iconAndScore,
              hit.document_id && 'text-primary underline-offset-2 group-hover:underline'
            )}
          >
            {normalizeDisplayFilename(hit.filename)}
          </span>
          {hit.document_id && (
            <ExternalLinkIcon className="size-3 shrink-0 text-muted-foreground" />
          )}
          <span className={HIT_META_GROUP_CLASS}>
            {hit.label && <LabelPill label={hit.label} />}
            <span
              className={cn(
                'ml-auto rounded px-1.5 py-0.5 font-mono text-[10px]',
                scoreColor(hit.score)
              )}
              title="RRF score"
            >
              {hit.score.toFixed(3)}
            </span>
          </span>
        </div>
        {hit.snippet && (
          <p
            className="line-clamp-3 whitespace-pre-wrap text-left text-xs text-muted-foreground"
            // Typesense highlights wrap matches in <mark> tags — preserve them.
            // biome-ignore lint/security/noDangerouslySetInnerHtml: snippet is sanitized by Typesense
            dangerouslySetInnerHTML={{ __html: hit.snippet }}
          />
        )}
        {hit.storage_path && (
          <p className="truncate text-[11px] text-muted-foreground/80">{hit.storage_path}</p>
        )}
      </div>
    </HitButton>
  )
}

function ChunkHitRow({
  hit,
  onOpenDocument,
}: {
  hit: LakeChunkHit
  onOpenDocument: (documentId: string) => void
}) {
  const hasPage = hit.page != null && hit.page !== ''
  return (
    <HitButton
      onClick={() => hit.document_id && onOpenDocument(hit.document_id)}
      disabled={!hit.document_id}
    >
      <FileTextIcon className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" />
      <div className="flex min-w-0 flex-col gap-1 max-md:flex-1">
        <div className="flex flex-wrap items-center gap-1.5 text-xs">
          <span
            className={cn(
              'truncate font-medium',
              hit.label || hasPage ? HIT_NAME_ROOM.icon : HIT_NAME_ROOM.iconAndScore,
              hit.document_id && 'text-primary underline-offset-2 group-hover:underline'
            )}
          >
            {normalizeDisplayFilename(hit.filename)}
          </span>
          {hit.document_id && (
            <ExternalLinkIcon className="size-3 shrink-0 text-muted-foreground" />
          )}
          <span className={HIT_META_GROUP_CLASS}>
            {hit.label && <LabelPill label={hit.label} />}
            {hasPage && (
              <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
                page {hit.page}
              </span>
            )}
            <span
              className={cn(
                'ml-auto rounded px-1.5 py-0.5 font-mono text-[10px]',
                scoreColor(hit.score)
              )}
              title="RRF score"
            >
              {hit.score.toFixed(3)}
            </span>
          </span>
        </div>
        {hit.snippet && (
          <p
            className="line-clamp-3 whitespace-pre-wrap text-left text-xs text-muted-foreground"
            // biome-ignore lint/security/noDangerouslySetInnerHtml: snippet is sanitized by Typesense
            dangerouslySetInnerHTML={{ __html: hit.snippet }}
          />
        )}
      </div>
    </HitButton>
  )
}

function Section({
  title,
  count,
  children,
}: {
  title: string
  count: number
  children: ReactNode
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center gap-2 px-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
        <span>{title}</span>
        <span className="rounded-sm bg-muted/50 px-1.5 py-0.5 font-medium tabular-nums">
          {count}
        </span>
      </div>
      <ul className="flex flex-col gap-1.5">{children}</ul>
    </div>
  )
}

function HitButton({
  children,
  onClick,
  disabled,
}: {
  children: ReactNode
  onClick: () => void
  disabled?: boolean
}) {
  return (
    <li>
      {disabled ? (
        <div className="rounded-md border border-border px-2.5 py-2">
          <div className="flex items-start gap-2">{children}</div>
        </div>
      ) : (
        <button
          type="button"
          onClick={onClick}
          className="group w-full cursor-pointer rounded-md border border-border px-2.5 py-2 text-left transition-colors hover:border-primary hover:bg-accent hover:text-accent-foreground focus:outline-none focus:ring-2 focus:ring-ring"
        >
          <div className="flex items-start gap-2">{children}</div>
        </button>
      )}
    </li>
  )
}

function LabelPill({ label }: { label: string }) {
  return (
    <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
      {label}
    </span>
  )
}
